// Build a bundle's branches and bring them up on the resolved GPU device.
//
// WHY THIS EXISTS. This sequence -- construct one `OGSBranch` per branch, publish
// them BEFORE any session exists, init sessions, resolve the lift device, demote
// on a device mismatch, then init the GPU half -- was written out three times:
// the live-rig pipeline and the static-neutral pipeline in `OGSModel.jsx`, and
// `neutralBranchSystem.ts` for the eye-test harness. They differ in three things
// (the extra `live` fields, packed vs SoA lifting, and what they log) and in
// nothing else, so those are the parameters and the rest is shared.
//
// TWO-PHASE INIT, AND THE ORDER IS LOAD-BEARING (see inference/gpuDevice.ts):
// `prepareOgsLiftDevice` must run BEFORE the first session create, and
// `resolveOgsLiftDevice` AFTER it, because the second reads the device ORT
// actually built. Phase 1 is sessions only; phase 2 is plücker + lifter on the
// device that came back. Collapsing them silently produces a cross-device
// GPUBuffer.
//
// PUBLISHING BEFORE INIT IS ALSO LOAD-BEARING. Teardown releases only what it can
// see, so a cancel partway through a 300 MB load would leak every decoder session
// already created. `onBranches` is called with the array before the first
// `init()`, not after the loop.

import { OGSBranch, type OGSBranchLiveOptions } from "./nodes/OGSBranch";
import { branchPortNames } from "./branchPorts";
import { prepareOgsLiftDevice, resolveOgsLiftDevice } from "../inference/gpuDevice";
import type { OgsLiftPrepared, OgsLiftDevice } from "../inference/gpuDevice";
import type { BranchAssets } from "../assets/loadMultiRegionScene";

/** ORT as this module uses it — the same shape gpuDevice.ts takes. */
type OrtLike = Parameters<typeof resolveOgsLiftDevice>[0];

/** The per-branch context `OGSBranch.init`/`initGpu` receive. */
interface BranchContext {
  ort: OrtLike;
  uvRes: number;
  decoderRes: number;
  rigDim: number;
  /** The page's one device (or null with no WebGPU). NOT gated on the IO — see NodeContext. */
  device: GPUDevice | null;
  gpuBufferIo: boolean;
}

export interface BuildBranchesOptions {
  /** Parsed branch assets, in the manifest's deterministic order. */
  branchAssets: BranchAssets[];
  ort: OrtLike;
  /** The result of `prepareOgsLiftDevice`, already awaited by the caller — it has
   *  to happen before the first session create and callers do other work there. */
  prepared: OgsLiftPrepared;
  /** Node id/member prefix. `branch` for the avatar paths, `neutral` for the harness. */
  idPrefix?: string;
  /** Per-branch `live` fields this caller adds on top of the shared ones. */
  live: (assets: BranchAssets, index: number) => Omit<OGSBranchLiveOptions, "assets">;
  /** false lifts SoA — the phase-3 GLB drive culls over-stretched splats through
   *  the SoA fields, which a packed lift leaves stale. */
  packed?: boolean;
  /** Called with the branch array BEFORE any session is built (teardown safety). */
  onBranches?: (branches: OGSBranch[]) => void;
  /** Per-branch, after its GPU half is up. For the caller's own ready line. */
  onReady?: (branch: OGSBranch, info: { ioMode: string; demoted: boolean }) => void;
  /** Checked after every await; returning true abandons the build. */
  isCancelled?: () => boolean;
}

export interface BuiltBranches {
  branches: OGSBranch[];
  lift: OgsLiftDevice;
  /** The device the lift runs on — what a consumer needs to build a compute pass
   *  over the branches' buffers. Previously `sharedDevice`, which went null
   *  whenever the decoder IO was off and so refused those consumers a device that
   *  was right there. */
  device: GPUDevice | null;
  /** `gpu-buffer` when the decoders hand the lift their outputs on the device. */
  ioMode: "gpu-buffer" | "cpu";
  /** The same context the branches were initialised with — `OGSRenderer.init`
   *  needs one, and building a second by hand is how the two drift. */
  contextFor: (branch: OGSBranch, device?: GPUDevice | null) => BranchContext;
}

/** @returns null iff the caller cancelled partway through. */
export async function buildBranches(opts: BuildBranchesOptions): Promise<BuiltBranches | null> {
  const {
    branchAssets, ort, prepared, idPrefix = "branch", live,
    packed = true, onBranches, onReady, isCancelled = () => false,
  } = opts;

  const branches = branchAssets.map((assets, index) => new OGSBranch({
    id: `${idPrefix}_${assets.name}`,
    name: assets.name,
    members: [`${idPrefix}_${assets.name}.geom`, `${idPrefix}_${assets.name}.appr`],
    // The renderer's input-port pair for this region — the same names
    // plugins/ogs-renderer/plugin.json declares (graph/branchPorts.ts).
    ports: branchPortNames(assets.name, index),
    live: { assets, ...live(assets, index) },
  }));

  onBranches?.(branches);

  const contextFor = (branch: OGSBranch, device: GPUDevice | null): BranchContext => ({
    ort, uvRes: branch.uvRes, decoderRes: branch.decoderRes, rigDim: branch.rigDim,
    device, gpuBufferIo: prepared.shared,
  });

  // Phase 1 — sessions only; ORT's device does not exist yet.
  for (const branch of branches) {
    await branch.init(contextFor(branch, null));
    if (isCancelled()) return null;
  }

  const lift = await resolveOgsLiftDevice(ort, prepared);
  if (isCancelled()) return null;

  // ORT built a device the lifter cannot share (dual-GPU powerPreference is
  // best-effort): the gpu-buffer hints are baked in at create time, so the only
  // repair is to download every output. Loud, never silent.
  const demoted = !!lift?.demoted;
  if (demoted) for (const branch of branches) branch.setDownloadOutputs(true);
  const device = lift?.device ?? null;
  const ioMode: "gpu-buffer" | "cpu" = prepared.shared && !demoted ? "gpu-buffer" : "cpu";

  // Phase 2 — plücker + lifter on the resolved device.
  for (const branch of branches) {
    await branch.initGpu(contextFor(branch, device), lift.device, packed ? undefined : { packed: false });
    if (isCancelled()) return null;
    onReady?.(branch, { ioMode, demoted });
  }

  return {
    branches, lift, device, ioMode,
    contextFor: (branch: OGSBranch, on: GPUDevice | null = device) => contextFor(branch, on),
  };
}

/** The branch list as `OGSRenderer` sources. The third verbatim copy of this
 *  (after the spine itself): the three call sites differed only in WHICH lifter
 *  they hand over — the live one, or a wrapper that adds `skipUvUpload` and the
 *  over-stretch cull for the skeleton drive.
 *
 *  The null check is not defensive noise. `initGpu` always leaves a lifter, so a
 *  missing one means the two-phase order broke — and the symptom of presenting a
 *  source with no lifter is an EMPTY render with nothing logged. */
export function renderSources(
  branches: OGSBranch[],
  lifterFor: (branch: OGSBranch, index: number) => unknown = (branch) => branch.lifter,
): unknown[] {
  return branches.map((branch, index) => {
    const lifter = lifterFor(branch, index);
    if (!lifter) throw new Error(`render sources: branch '${branch.name}' has no lifter`);
    return {
      name: branch.name,
      ports: branch.outputs.map((port) => port.name),
      lifter,
      getVerts: () => branch.verts,
      capacity: branch.uvRes * branch.uvRes,
    };
  });
}

export { prepareOgsLiftDevice };
